import type { ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './queryClient';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FeatureFlagProvider, useFeatureFlags } from './contexts/FeatureFlagContext';
import { landingFor } from './utils/landing';
import { ToastProvider } from './contexts/ToastContext';
import { ProtectedLayout, PublicLayout, AdminRoute } from './layouts';
import FeatureGate from './components/FeatureGate';
import CookieConsentBanner from './components/CookieConsentBanner';
// Initialize i18next (react-i18next)
import './i18n';
import HomePage from './pages/HomePage';
import HomeV2Layout from './pages/home-v2/HomeV2Layout';
import HomeV2Page from './pages/home-v2/HomeV2Page';
import CompanyV2Page from './pages/home-v2/CompanyV2Page';
import SecurityV2Page from './pages/home-v2/SecurityV2Page';
import { DRAFT_SITE, HOMEPAGE_SITE } from './pages/home-v2/edition';
import LoginPage from './pages/LoginPage';
import ConnectionsPage from './pages/ConnectionsPage';
import IntegrationsPage from './pages/IntegrationsPage';
import DocumentsPage from './pages/documents';
import AdminPage from './pages/AdminPage';
import AdminMessagesPage from './pages/AdminMessagesPage';
import AdminAuditDrilldown from './pages/AdminAuditDrilldown';
import FeatureFlagsPage from './pages/FeatureFlagsPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import SettingsPage from './pages/SettingsPage';
import WorkspacePage from './pages/WorkspacePage';
import FarmPage from './pages/farm';
import AgentPage from './pages/agent';
import ConnectPage from './pages/ConnectPage';
import HistoryPage from './pages/HistoryPage';
import PruefstatusPage from './pages/PruefstatusPage';
import NotFoundPage from './pages/NotFoundPage';
import AccountDeletedPage from './pages/AccountDeletedPage';
import ImpressumPage from './pages/ImpressumPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import InviteRequiredPage from './pages/InviteRequiredPage';
import AppsPage from './pages/apps';
import { hasAccessibleApp } from './pages/apps/registry';
import LabourRoute from './pages/apps/labour/LabourRoute';
import MorgenlagePage from './pages/apps/morgenlage/MorgenlagePage';
import GutschriftCheckPage from './pages/apps/gutschrift-check/GutschriftCheckPage';
import GutschriftCheckFeedPage from './pages/apps/gutschrift-check/GutschriftCheckFeedPage';
import LiquiditaetsplanungPage from './pages/apps/liquiditaetsplanung/LiquiditaetsplanungPage';
import DeckungsbeitragPage from './pages/apps/deckungsbeitrag/DeckungsbeitragPage';
import ErnteprognosePage from './pages/apps/ernteprognose/ErnteprognosePage';
import PreisspiegelPage from './pages/apps/preisspiegel/PreisspiegelPage';
import './App.css';


function RootRedirect() {
  const { isAuthenticated, isLoading, userProfile } = useAuth();
  // #2074: the public site prerenders the homepage at `/` (website v2 since
  // HOF-662). Only a KNOWN-authenticated user is redirected to their workspace;
  // everyone else — anonymous visitors AND anyone while the session check is
  // still loading — sees the homepage at `/` directly. This aligns the client
  // route with the prerendered output, so JS visitors no longer get a blank
  // content-flash (previously: `return null` while loading → `<Navigate to="/home">`).
  //
  // HOF-696 D5: the target is the seat's landing (`/apps/labour` for a worker,
  // `/documents` otherwise), decided only after loading — so it reads the
  // fetched profile, never a stale one.
  if (!isLoading && isAuthenticated) {
    return <Navigate to={landingFor(userProfile)} replace />;
  }
  return <HomeV2Page />;
}

/**
 * HOF-696 D5 — the `/apps` gate: an active farm app the seat may open
 * (`active_farm.apps` × `active_farm.permissions`), or the existing per-role
 * app flags. Waits for the flags like `FeatureGate` does, so nothing flashes.
 */
function AppsGate({ children }: { children: ReactNode }) {
  const { hasFlag, isLoaded } = useFeatureFlags();
  const { userProfile } = useAuth();
  if (!isLoaded) return null;
  if (!hasAccessibleApp(hasFlag, userProfile?.active_farm)) {
    return <Navigate to="/documents" replace />;
  }
  return <>{children}</>;
}

function AppContent() {
  return (
    <Routes>
      {/* Website v2 (hofsync-org marketing/website-refresh) has its own header
          and footer, so PublicLayout stays as it is. HOF-662: it is the homepage,
          in HOMEPAGE_EDITION; the draft at /home_v2 keeps the full edition. */}
      <Route element={<HomeV2Layout site={HOMEPAGE_SITE} />}>
        {/* Root: public landing for anonymous visitors (matches the
            prerendered `/`); known-authenticated users redirect to /documents. */}
        <Route path="/" element={<RootRedirect />} />
        <Route path="/unternehmen" element={<CompanyV2Page />} />
        <Route path="/company" element={<CompanyV2Page />} />
        <Route path="/sicherheit" element={<SecurityV2Page />} />
        <Route path="/security" element={<SecurityV2Page />} />
      </Route>
      <Route path="/home_v2" element={<HomeV2Layout site={DRAFT_SITE} />}>
        <Route index element={<HomeV2Page />} />
        <Route path="unternehmen" element={<CompanyV2Page />} />
        <Route path="company" element={<CompanyV2Page />} />
        <Route path="sicherheit" element={<SecurityV2Page />} />
        <Route path="security" element={<SecurityV2Page />} />
      </Route>

      {/* Public routes with Header + Footer */}
      <Route element={<PublicLayout />}>
        {/* The homepage before website v2 (HOF-662), kept reachable. */}
        <Route path="/home" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/impressum" element={<ImpressumPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        {/* #2074 — German public URL for the Datenschutzerklärung. Serves the
            same PrivacyPage (canonical → /privacy); prerendered + crawlable. */}
        <Route path="/datenschutz" element={<PrivacyPage />} />
        <Route path="/account-deleted" element={<AccountDeletedPage />} />
        <Route path="/invite-required" element={<InviteRequiredPage />} />
        {/* Catch-all 404 route */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* HOF-696 D5/D6 — the labour app picks its own chrome: a self-only worker
          seat gets the WorkerLayout (no chat, search, focus rail or settings), an
          office seat the normal ProtectedLayout. So it sits OUTSIDE the
          ProtectedLayout route group; LabourRoute does the auth check itself. */}
      <Route path="/apps/labour" element={<LabourRoute />} />

      {/* Protected routes with MainLayout */}
      <Route element={<ProtectedLayout />}>
        {/* Feature-flag-gated pages (mock UI or twin) */}
        <Route
          path="/pruefstatus"
          element={
            <FeatureGate anyOf={['mock_ui.pruefstatus', 'twin.pruefstatus']} redirectTo="/documents">
              <PruefstatusPage />
            </FeatureGate>
          }
        />
        <Route
          path="/workspace"
          element={
            <FeatureGate anyOf={['mock_ui.workspace', 'twin.events']} redirectTo="/documents">
              <WorkspacePage />
            </FeatureGate>
          }
        />
        <Route
          path="/farm"
          element={
            <FeatureGate anyOf={['mock_ui.farm', 'twin.entities']} redirectTo="/documents">
              <FarmPage />
            </FeatureGate>
          }
        />
        {/* FR-015: the /agent AI-assistant workspace (projects + conversations
            over the KI-Hofassistent). Un-gated, like the chat panel's KI tab. */}
        {/* RFC 8628 verification page (A3). `OAUTH_VERIFICATION_URI` points
            here (https://hofsync.de/connect in production), so this path is a
            DEPLOYED CONTRACT — every already-installed agent prints it on the
            farm PC. Renaming this route breaks pairing for all of them. */}
        <Route path="/connect" element={<ConnectPage />} />
        <Route path="/agent" element={<AgentPage />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route
          path="/apps"
          element={
            <AppsGate>
              <AppsPage />
            </AppsGate>
          }
        />
        {/* Morgenlage (h-cockpit-daily-dashboard-redesign) — the Cockpit
            successor: the farmer's daily briefing, currently the concept view
            with Beispieldaten. The flag id stays `apps.cockpit` (baked into
            migration 037); the old path redirects so bookmarks keep working. */}
        <Route
          path="/apps/morgenlage"
          element={
            <FeatureGate flag="apps.cockpit" redirectTo="/documents">
              <MorgenlagePage />
            </FeatureGate>
          }
        />
        <Route path="/apps/cockpit" element={<Navigate to="/apps/morgenlage" replace />} />
        {/* #1289 Part C — the farmer-facing feed view is the default
            landing ("Öffnen"); Decision 5 accepts that pre-existing
            ?ym= bookmarks now open the feed instead of the canvas. */}
        <Route
          path="/apps/gutschrift-check"
          element={
            <FeatureGate flag="apps.gutschrift_check" redirectTo="/documents">
              <GutschriftCheckFeedPage />
            </FeatureGate>
          }
        />
        {/* Data-administration entry point for the match-graph canvas
            (#1289, "Editor" tile CTA). Same flag as the base route. */}
        <Route
          path="/apps/gutschrift-check/editor"
          element={
            <FeatureGate flag="apps.gutschrift_check" redirectTo="/documents">
              <GutschriftCheckPage />
            </FeatureGate>
          }
        />
        {/* #1315 — Liquiditätsplanung v1 (Nina pilot). Flag granted to
            a single-user pilot role only; skeleton until subtask 04. */}
        <Route
          path="/apps/liquiditaetsplanung"
          element={
            <FeatureGate flag="apps.liquiditaetsplanung" redirectTo="/documents">
              <LiquiditaetsplanungPage />
            </FeatureGate>
          }
        />
        {/* #1781 — Deckungsbeitrag v1: ranked db1_per_ha margin view over
            the R2–R6 engine's derived facts. Flag default off in prod. */}
        <Route
          path="/apps/deckungsbeitrag"
          element={
            <FeatureGate flag="apps.deckungsbeitrag" redirectTo="/documents">
              <DeckungsbeitragPage />
            </FeatureGate>
          }
        />
        {/* #2341 — Ernteprognose v1: yield Plan/Prognose/Ist over time per crop
            with a horizon overlay + Ampel verdict. First production-output
            surface. Flag default off in prod. */}
        <Route
          path="/apps/ernteprognose"
          element={
            <FeatureGate flag="apps.ernteprognose" redirectTo="/documents">
              <ErnteprognosePage />
            </FeatureGate>
          }
        />
        {/* #2388 — Preisspiegel v1: stock-market-styled price browser over the
            farm-product catalog, rendering the tenant's own price_observation
            facts (#2394). Flag default off in prod (pilot app-gate). */}
        <Route
          path="/apps/preisspiegel"
          element={
            <FeatureGate flag="apps.preisspiegel" redirectTo="/documents">
              <PreisspiegelPage />
            </FeatureGate>
          }
        />

        {/* Admin routes (role-based, not flag-based) */}
        <Route element={<AdminRoute />}>
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/messages" element={<AdminMessagesPage />} />
          <Route
            path="/admin/users/:userId/audit-events"
            element={<AdminAuditDrilldown />}
          />
          <Route
            path="/admin/features"
            element={
              <FeatureGate flag="admin.feature_flags" redirectTo="/admin">
                <FeatureFlagsPage />
              </FeatureGate>
            }
          />
        </Route>

        {/* Routes available to all authenticated users */}
        <Route path="/documents" element={<DocumentsPage />} />
        {/* HOF-627 settings refactor: one pane per section (`/settings/team`, …). */}
        <Route path="/settings/:section?" element={<SettingsPage />} />
        {/* @deprecated - Will be removed after Google Drive migration to IntegrationsPage */}
        <Route path="/connections" element={<ConnectionsPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/oauth/callback/google-drive" element={<OAuthCallbackPage />} />
        <Route path="/oauth/callback/:provider" element={<OAuthCallbackPage />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <FeatureFlagProvider>
            <Router>
              <AppContent />
              {/* #3131 — mounted here, as a sibling of the route table, because
                  this is the only node above BOTH PublicLayout and
                  ProtectedLayout: the notice must reach authenticated users too.
                  Inside <Router> so it can <Link> to /privacy. Deliberately NOT
                  added to prerender/entry.tsx — the baked HTML stays
                  banner-free and the notice appears when the SPA mounts. */}
              <CookieConsentBanner />
            </Router>
          </FeatureFlagProvider>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}

export default App;
